import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Observable, Subscription, combineLatest, firstValueFrom, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

import { CalsiumListConfig, CalsiumListSelectItem, ColumnDataType } from '../../../calsiumlist/calsiumlist.component.model';
import { Paging, CommonStateService, ScaniaUnit, MasterDataStateService } from '../../../../state';
import { OrchestratorStateService } from '../../../../state/orchestrator/service';
import { StepFaultSearchForm } from '../../../../state/orchestrator/state-models/step-fault-search-form';
import { CalsiumDateFormatterPipe } from '../../../shared/calsium-date.pipe';
import { CalsiumDateFormatter } from '../../../shared/calsium-date-formatter';
import { StepFaultView } from '../../../../state/orchestrator/state-models/step-fault-view';
import { MissingPriceStepFault } from '../../../../state/orchestrator/state-models/step-fault';
import { ErrorHandlerService } from 'src/app/errorhandler/error-handler.service';
import { ErrorTypeEnum } from 'src/app/shared/enums.model';

@Component({
    selector: 'cal-stepfault-list',
    templateUrl: './stepFault-list.component.html',
    styleUrls: ['./stepFault-list.component.css'],
    standalone: false
})
export class StepFaultListComponent implements OnInit, OnDestroy {

    resources: any = this.commonService.getResources();
    dataList: Observable<StepFaultView[]>;
    listConfig: CalsiumListConfig;
    paging: Observable<Paging>;
    totalCount: Observable<number>;
    subscriptions = new Subscription();
    isLoading: Observable<boolean>;
    sortingExpression: Observable<string>;
    filterExpression: Observable<string>;
    searchForm: UntypedFormGroup;
    stepFaultSearchForm: Observable<StepFaultSearchForm>;
    scaniaUnitList: Observable<ScaniaUnit[]>;
    scaniaUnitOptions: ScaniaUnit[] = [];
    private previousScaniaUnitIds: number[] = [];

    constructor(
        private apiService: OrchestratorStateService,
        private masterDataService: MasterDataStateService,
        private errHandler: ErrorHandlerService,
        private commonService: CommonStateService,
        private formBuilder: UntypedFormBuilder
    ) { }

    ngOnInit() {
        this.buildSearchForm();
        this.isLoading = this.commonService.getLoading();
        this.paging = this.apiService.getStepFaultPaging();
        this.totalCount = this.apiService.getStepFaultTotalCount();
        this.sortingExpression = this.apiService.getStepFaultSortingExpression();
        this.filterExpression = this.apiService.getStepFaultFilterExpression();
        this.stepFaultSearchForm = this.apiService.getStepFaultSearchForm();
        this.scaniaUnitList = this.masterDataService.getScaniaAllocationUnits();
        this.subscriptions.add(
            this.scaniaUnitList.subscribe((scaniaUnits) => {
                this.scaniaUnitOptions = scaniaUnits || [];
            })
        );

        let pageInitialLoad = true;
        this.subscriptions.add(
            combineLatest(this.stepFaultSearchForm, this.sortingExpression, this.filterExpression, this.paging).pipe(
                debounceTime(0),
                switchMap(([searchForm, sort, filter, paging]) => {
                    if (searchForm) {
                        const selectedScaniaUnitIds = Array.isArray(searchForm.selectedScaniaUnitIds)
                            ? searchForm.selectedScaniaUnitIds
                            : (searchForm.selectedScaniaUnitIds != null ? [searchForm.selectedScaniaUnitIds] : []);
                        this.fillSearchForm(searchForm.fromDate, searchForm.toDate, selectedScaniaUnitIds);
                    }
                    if (!pageInitialLoad) {
                        return this.apiService.loadStepFaultData(searchForm, paging, sort, filter);
                    }
                    pageInitialLoad = false;
                    return new Promise<void>(null);
                })
            ).subscribe()
        );

        this.dataList = this.apiService.getStepFault();
        this.buildList();
    }

    ngOnDestroy() { this.subscriptions.unsubscribe(); }

    buildSearchForm() {
        this.searchForm = this.formBuilder.group({
            startDate: [this.toDateValue(CalsiumDateFormatter.getDateOrDefault(null, true)), []],
            endDate: [this.toDateValue(CalsiumDateFormatter.getDateOrDefault(null, false)), []],
            selectedScaniaUnitIds: [[], <any>Validators.required]
        });
    }

    fillSearchForm(fromDate: Date = null, toDate: Date = null, scaniaUnitIds: Array<number | string> = []) {
        // mat-option values are numbers, so the control value must be numeric to keep the selection rendered
        const selectedScaniaUnitIds = Array.isArray(scaniaUnitIds)
            ? scaniaUnitIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
            : [];
        this.previousScaniaUnitIds = selectedScaniaUnitIds;

        this.searchForm.patchValue({
            startDate: this.toDateValue(CalsiumDateFormatter.getDateOrDefault(fromDate, true)),
            endDate: this.toDateValue(CalsiumDateFormatter.getDateOrDefault(toDate, false)),
            selectedScaniaUnitIds
        });
    }

    // the range picker works with Date objects while the rest of the flow keeps yyyy-MM-dd strings
    private toDateValue(value: Date | string): Date {
        if (!value) {
            return null;
        }
        if (value instanceof Date) {
            return value;
        }
        const [year, month, day] = String(value).split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    private toDateOnlyString(value: Date | string): string {
        if (!value) {
            return null;
        }
        if (typeof value === 'string') {
            return value;
        }
        const month = `${value.getMonth() + 1}`.padStart(2, '0');
        const day = `${value.getDate()}`.padStart(2, '0');
        return `${value.getFullYear()}-${month}-${day}`;
    }

    getSelectedScaniaUnits(): Array<{ id: number | string; name: string }> {
        const selectedValues = this.searchForm?.get('selectedScaniaUnitIds')?.value || [];
        if (!selectedValues.length) {
            return [];
        }

        if (selectedValues.some((value) => Number(value) === -1)) {
            return [{ id: -1, name: '*All*' }];
        }

        return selectedValues
            .map((value) => {
                const matchingUnit = this.scaniaUnitOptions.find((unit) => String(unit.id) === String(value));
                if (!matchingUnit) {
                    return null;
                }

                return { id: matchingUnit.id, name: matchingUnit.name };
            })
            .filter((unit) => unit !== null);
    }

    onScaniaUnitSelectionChange(selectedValues: Array<number | string>): void {
        const selectedIds = selectedValues.map((value) => Number(value)).filter((id) => !Number.isNaN(id));
        const hasAll = selectedIds.includes(-1);
        const hadAll = this.previousScaniaUnitIds.includes(-1);
        const allUnitIds = this.scaniaUnitOptions.map((unit) => Number(unit.id));

        if (hadAll && !hasAll) {
            this.previousScaniaUnitIds = [];
            this.searchForm.patchValue({ selectedScaniaUnitIds: [] }, { emitEvent: false });
            return;
        }

        if (hasAll && !hadAll) {
            this.searchForm.patchValue({ selectedScaniaUnitIds: [-1, ...allUnitIds] });
            this.previousScaniaUnitIds = [-1, ...allUnitIds];
            return;
        }

        const normalizedIds = hasAll
            ? selectedIds.filter((id) => id !== -1)
            : selectedIds;
        this.previousScaniaUnitIds = normalizedIds;
        this.searchForm.patchValue({ selectedScaniaUnitIds: normalizedIds }, { emitEvent: false });
    }

    // mat-select opens on the trigger's mousedown/click, so chip actions must swallow both
    onChipActionMouseDown(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
    }

    removeSelectedScaniaUnit(selectedId: number | string): void {
        const currentValues = this.searchForm.get('selectedScaniaUnitIds')?.value || [];
        const updatedValues = currentValues.filter((value) => String(value) !== String(selectedId));
        this.searchForm.patchValue({ selectedScaniaUnitIds: updatedValues });
    }

    clearSelectedScaniaUnits(): void {
        this.searchForm.patchValue({ selectedScaniaUnitIds: [] });
    }

    onPaging(page: number) {
        this.apiService.goToStepFaultPage(page);
    }

    onSort(sortExpression: string) {
        this.apiService.sortStepFault(sortExpression);
    }

    onFilter(filterExpression: string) {
        this.apiService.filterStepFault(filterExpression);
    }

    onClearFilterAndSorting() {
        this.apiService.clearFilterAndSorting();
    }

        async onExportToExcel() {
            if (!this.isValidForm()) {
                return;
            }

            const fromDate = this.toDateOnlyString(this.searchForm.value.startDate);
            const toDate = this.toDateOnlyString(this.searchForm.value.endDate);
            const selectedScaniaUnits = this.searchForm.value.selectedScaniaUnitIds || [];
            const selectedScaniaUnitIds = Array.isArray(selectedScaniaUnits)
                ? selectedScaniaUnits.map((value) => value === '-1' || value === -1 ? -1 : Number(value))
                    .filter((value) => !Number.isNaN(value))
                : [];
            const scaniaUnitId = selectedScaniaUnitIds.includes(-1) ? [-1] : selectedScaniaUnitIds;
            const scaniaUnitNames = scaniaUnitId.includes(-1)
                ? 'All'
                : scaniaUnitId
                    .map((id) => this.scaniaUnitOptions.find((unit) => Number(unit.id) === id)?.name)
                    .filter((name) => !!name)
                    .join('_');
            const [order, filter] = await Promise.all([
                firstValueFrom(this.sortingExpression),
                firstValueFrom(this.filterExpression)
            ]);

            const payload = {
                from: new Date(fromDate).toISOString(),
                to: new Date(toDate).toISOString(),
                scaniaUnitId,
                order,
                filter
            };

            return this.apiService.exportStepFaultExcel(`StepFaults_${scaniaUnitNames}_${fromDate}_${toDate}`, payload);
        }

    buildList() {
        this.listConfig = new CalsiumListConfig();
        this.listConfig.ShowListCaption = true;
        this.listConfig.ListCaption = this.resources.FlowErrorPageTitle;
        this.listConfig.ShowFilterRow = true;
        this.listConfig.ShowSumRow = false;
        this.listConfig.HasSubComponent = false;
        this.listConfig.ParentComponent = this;
        this.listConfig.ShowExportToExcel = true;
        this.listConfig.AllowAdd = false;
        this.listConfig.AllowEdit = false;
        this.listConfig.AllowDelete = false;

        let  column = this.listConfig.AddListColumn(ColumnDataType.list, 'faultCategory', this.resources.FaultCategory);
        column.AllowFilter = true;
        column.Sortable = false;        
        column.FilterOptions.FilterListItems = of(CalsiumListSelectItem.getInstanceArr(['Business', 'System'], [this.resources.Business, this.resources.System]));
        column.FilterOptions.AddEmptyDropDownListItem = false;

        column = this.listConfig.AddListColumn(ColumnDataType.anchor, 'details', this.resources.ReferenceNumber);
        column.AllowFilter = true;
        column.Sortable = true;
        column.IsAnchor = this.isAnchor;
        column.GetAnchorLink = this.GetAnchorLink;

        column = this.listConfig.AddListColumn(ColumnDataType.string, 'stepDescription', this.resources.FlowStep);
        column.AllowFilter = true;
        column.Sortable = true;

        column = this.listConfig.AddListColumn(ColumnDataType.string, 'message', this.resources.Message);
        column.AllowFilter = true;
        column.Sortable = true;

        column = this.listConfig.AddListColumn(ColumnDataType.date, 'startDate', this.resources.ExecutionDate);
        column.AllowFilter = true;
        column.Sortable = true;
        column.DisplayPipe = new CalsiumDateFormatterPipe();
    }

    onSearch() {
        if (this.isValidForm()) {
            if (this.searchForm.valid) {
                const fromDate = this.toDateOnlyString(this.searchForm.value.startDate);
                const toDate = this.toDateOnlyString(this.searchForm.value.endDate);
                const selectedScaniaUnits = this.searchForm.value.selectedScaniaUnitIds || [];
                const selectedScaniaUnitIds = Array.isArray(selectedScaniaUnits)
                    ? selectedScaniaUnits.map((value) => value === '-1' || value === -1 ? -1 : Number(value))
                    : [];
                const param: StepFaultSearchForm = {
                    fromDate: <any>fromDate,
                    toDate: <any>toDate,
                    selectedScaniaUnitIds: selectedScaniaUnitIds.filter((value) => value !== null && value !== undefined && !Number.isNaN(value))
                };
                this.apiService.changeStepFaultSearchForm(param);
            }
        }
    }

    isAnchor(rowData: StepFaultView): boolean {
        if (rowData.type === MissingPriceStepFault) {
            return true;
        }
        return false;
    }

    GetAnchorLink(rowData: StepFaultView): string {
        if (rowData.type === MissingPriceStepFault) {
            return `/material/${rowData.referenceValue}`;
        }
        // other links in fault screen will be added here
    }

    isValidForm(): boolean {
        let valid = true;
        const startDate = this.searchForm.get('startDate')?.value;
        const endDate = this.searchForm.get('endDate')?.value;

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            this.errHandler.emitError(
                this.resources.ValidFromValidToCorrect,
                this.resources.Error,
                ErrorTypeEnum.Error);
            valid = false;
        }

        return valid;
    }

}
